import {
  faArrowUpRightFromSquare,
  faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { WarningSolid } from '@wfp/icons-react';
import { Button } from '@wfp/react';
import DOMPurify from 'dompurify';
import type { FC } from 'react';
import React, { useEffect, useMemo, useRef } from 'react';

import { AiSourceReference } from 'src/redux/aiNarrative/types';
import { getPlainText } from 'src/redux/aiNarrative/utils';
import { countWords } from 'src/utils';

interface Props {
  draftHtml: string;
  currentContent?: string;
  references: AiSourceReference[];
  wordLimit: number;
  hasExistingContent: boolean;
  onAccept: () => void;
  onReject: () => void;
}

const DraftPreviewPanel: FC<Props> = ({
  draftHtml,
  currentContent,
  references,
  wordLimit,
  hasExistingContent,
  onAccept,
  onReject,
}) => {
  const sanitizedDraft = useMemo(
    () => DOMPurify.sanitize(draftHtml),
    [draftHtml],
  );

  const sanitizedCurrent = useMemo(
    () => (currentContent ? DOMPurify.sanitize(currentContent) : ''),
    [currentContent],
  );
  const showBefore = hasExistingContent && !!sanitizedCurrent;

  const draftWordCount = useMemo(
    () => countWords(getPlainText(sanitizedDraft)),
    [sanitizedDraft],
  );

  const acceptButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    acceptButtonRef.current?.focus();
  }, []);

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') onReject();
  }

  return (
    <div
      className="ai-draft-preview-panel"
      data-test-id="ai-draft-preview"
      onKeyDown={handleKeyDown}
    >
      {showBefore && (
        <>
          <div className="ai-draft-preview-header ai-draft-before-header">
            Current narrative
          </div>
          <div
            className="ai-draft-preview-content ai-draft-before-content"
            data-test-id="ai-draft-before"
            dangerouslySetInnerHTML={{ __html: sanitizedCurrent }}
          />
        </>
      )}
      <div className="ai-draft-preview-header">
        <FontAwesomeIcon icon={faWandMagicSparkles} aria-hidden="true" />
        AI-generated draft
      </div>
      <div
        className="ai-draft-preview-content"
        dangerouslySetInnerHTML={{ __html: sanitizedDraft }}
      />
      {hasExistingContent && (
        <div className="ai-draft-preview-note">
          Accepting will replace the current narrative content.
        </div>
      )}
      {draftWordCount > wordLimit && (
        <div
          className="ai-draft-preview-warning"
          data-test-id="ai-draft-word-warning"
        >
          <WarningSolid color="#E4002B" width="16" height="16" />
          The draft exceeds the word limit: {draftWordCount} / {wordLimit}
        </div>
      )}
      {references.length > 0 && (
        <div className="ai-draft-sources">
          <div className="ai-draft-sources-title">Sources</div>
          <ul>
            {references.map(reference =>
              reference.url ? (
                <li key={reference.id}>
                  <a
                    href={reference.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open ${reference.filename} in a new tab`}
                    data-test-id={`ai-source-${reference.id}`}
                  >
                    <FontAwesomeIcon
                      icon={faArrowUpRightFromSquare}
                      aria-hidden="true"
                    />
                    <span className="ai-draft-source-title">
                      {reference.filename}
                    </span>
                  </a>
                </li>
              ) : (
                <li
                  key={reference.id}
                  data-test-id={`ai-source-${reference.id}`}
                >
                  <span className="ai-draft-source-title">
                    {reference.filename}
                  </span>
                </li>
              ),
            )}
          </ul>
        </div>
      )}
      <div className="ai-draft-preview-actions">
        <Button
          kind="secondary"
          className="ai-draft-reject"
          onClick={onReject}
          data-test-id="ai-draft-reject"
        >
          Reject
        </Button>
        <Button
          ref={acceptButtonRef}
          className="ai-draft-accept"
          onClick={onAccept}
          data-test-id="ai-draft-accept"
        >
          Accept
        </Button>
      </div>
    </div>
  );
};

export default DraftPreviewPanel;
